import type { ReactNode } from "react"
import { Kbd } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"

type KeyTone = "move" | "modify" | "action" | "light"

const TONE_CLASS: Record<KeyTone, string> = {
  move: "border-sky-500/40 bg-sky-500/15 text-sky-800 dark:text-sky-100",
  modify: "border-amber-500/40 bg-amber-500/15 text-amber-900 dark:text-amber-100",
  action: "border-emerald-500/40 bg-emerald-500/15 text-emerald-800 dark:text-emerald-100",
  light: "border-violet-500/40 bg-violet-500/15 text-violet-800 dark:text-violet-100",
}

function HintKbd({ children, tone }: { children: ReactNode; tone: KeyTone }) {
  return (
    <Kbd className={cn("inline rounded-sm px-1.5 py-0.5 text-[10px] leading-none sm:inline", TONE_CLASS[tone])}>
      {children}
    </Kbd>
  )
}

function HintRow({ keys, text }: { keys: ReactNode; text: string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      <span className="inline-flex flex-wrap items-center gap-1">{keys}</span>
      <span className="text-ui-text-secondary">{text}</span>
    </div>
  )
}

export function LocationScene3dControlsHint() {
  const { t } = useTranslation(["workspace"])
  return (
    <div className="flex flex-col gap-2 text-[11px] leading-snug text-ui-text-primary">
      <div className="flex flex-col gap-1">
        <p className="font-semibold text-ui-text-primary">{t("locationsPage.scene3d.controlsHint.fly")}</p>
        <HintRow
          keys={
            <>
              <HintKbd tone="move">W</HintKbd>
              <HintKbd tone="move">A</HintKbd>
              <HintKbd tone="move">S</HintKbd>
              <HintKbd tone="move">D</HintKbd>
            </>
          }
          text={t("locationsPage.scene3d.controlsHint.move")}
        />
        <HintRow
          keys={
            <>
              <HintKbd tone="move">R</HintKbd>
              <HintKbd tone="move">F</HintKbd>
            </>
          }
          text={t("locationsPage.scene3d.controlsHint.upDown")}
        />
        <HintRow
          keys={
            <>
              <HintKbd tone="move">{t("locationsPage.scene3d.controlsHint.lmb")}</HintKbd>
              <span className="text-ui-text-muted">+</span>
              <HintKbd tone="move">drag</HintKbd>
            </>
          }
          text={t("locationsPage.scene3d.controlsHint.orbit")}
        />
        <HintRow
          keys={
            <>
              <HintKbd tone="move">{t("locationsPage.scene3d.controlsHint.lmb")}</HintKbd>
              <span className="text-ui-text-muted">+</span>
              <HintKbd tone="move">drag</HintKbd>
              <span className="text-ui-text-muted">+</span>
              <HintKbd tone="modify">Shift</HintKbd>
            </>
          }
          text={t("locationsPage.scene3d.controlsHint.faster")}
        />
      </div>
      <div className="flex flex-col gap-1">
        <p className="font-semibold text-ui-text-primary">{t("locationsPage.scene3d.controlsHint.record")}</p>
        <HintRow
          keys={<HintKbd tone="action">Space</HintKbd>}
          text={t("locationsPage.scene3d.controlsHint.playStop")}
        />
        <HintRow
          keys={
            <>
              <HintKbd tone="modify">Shift</HintKbd>
              <span className="text-ui-text-muted">+</span>
              <HintKbd tone="action">Space</HintKbd>
            </>
          }
          text={t("locationsPage.scene3d.controlsHint.recordToggle")}
        />
        <HintRow
          keys={<HintKbd tone="action">P</HintKbd>}
          text={t("locationsPage.scene3d.controlsHint.captureViewpoint")}
        />
      </div>
      <div className="flex flex-col gap-1">
        <p className="font-semibold text-ui-text-primary">{t("locationsPage.scene3d.controlsHint.trajectory")}</p>
        <HintRow
          keys={<HintKbd tone="action">T</HintKbd>}
          text={t("locationsPage.scene3d.controlsHint.trajectoryToggle")}
        />
        <HintRow
          keys={
            <>
              <HintKbd tone="modify">Shift</HintKbd>
              <span className="text-ui-text-muted">+</span>
              <HintKbd tone="action">drag</HintKbd>
            </>
          }
          text={t("locationsPage.scene3d.controlsHint.trajectoryShiftDrag")}
        />
      </div>
      <div className="flex flex-col gap-1">
        <p className="font-semibold text-ui-text-primary">{t("locationsPage.scene3d.controlsHint.light")}</p>
        <HintRow keys={<HintKbd tone="light">drag</HintKbd>} text={t("locationsPage.scene3d.controlsHint.lightDrag")} />
        <HintRow
          keys={
            <>
              <HintKbd tone="modify">Shift</HintKbd>
              <span className="text-ui-text-muted">+</span>
              <HintKbd tone="light">drag</HintKbd>
            </>
          }
          text={t("locationsPage.scene3d.controlsHint.lightShiftDrag")}
        />
      </div>
    </div>
  )
}
